import React, { useState, useMemo, useCallback } from 'react';
import { Modal, Button, Checkbox, TabPanel, Tabs } from 'tea-component';
import type { SortBy } from 'tea-component/lib/table/addons';
import CardCommon from '@op/components/CardCommon';
import type { operation } from '@op/components/CardCommon';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_U } from '@op/utils/format';
import { ClassSpecial } from '@op/constants/common';
import { DEFAULT_VISIBLE_COLUMNS, buildViewOptions } from '../../constants';
import { getVersionConfig } from '../../../config';
import VersionTable from './VersionTable';
import './index.scss';

/** 指标列配置（仅用于设置弹窗的 checkbox 选项） */
const METRIC_COLUMNS = [
  { key: 'rating', label: 'Rating' },
  { key: 'number_of_reviews', label: 'Number of Reviews' },
  { key: 'positive_reviews', label: 'Positive Reviews' },
  { key: 'positive_ratio', label: 'Positive Ratio' },
  { key: 'negative_reviews', label: 'Negative Reviews' },
  { key: 'negative_ratio', label: 'Negative Ratio' },
] as const;

interface VersionAnalysisTableProps {
  filter: Record<string, any>;
}

const VersionAnalysisTable: React.FC<VersionAnalysisTableProps> = ({
  filter,
}) => {
  const isConfigGame = filter.is_dune;
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const [settingVisible, setSettingVisible] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    ...DEFAULT_VISIBLE_COLUMNS,
  ]);
  const [stagingColumns, setStagingColumns] = useState<string[]>([
    ...DEFAULT_VISIBLE_COLUMNS,
  ]);

  // 配置游戏：从 versionConfig 动态生成 tab 选项
  const versionConfig = useMemo(
    () => getVersionConfig(filter?.edition_unified_id),
    [filter?.edition_unified_id],
  );

  const tabOptions = useMemo(
    () => (versionConfig ? buildViewOptions(versionConfig) : []),
    [versionConfig],
  );

  // 配置游戏：当前激活的 tab（默认第二个维度；非配置游戏 tabOptions 为空数组时回退 'release'）
  const [groupBy, setGroupBy] = useState<string>(
    tabOptions?.[1]?.value || 'release',
  );

  // 当前表格排序状态（由子组件 VersionTable 上报，用于下载时传递排序参数）
  const [tableSortInfo, setTableSortInfo] = useState<SortBy<any>>({
    by: 'release_date',
    order: 'desc',
  });

  // 设置弹窗：复选框选项
  const checkboxOptions = useMemo(
    () =>
      METRIC_COLUMNS.map((col) => ({
        value: col.key,
        label: formatMessage({ id: col.label }),
      })),
    [formatMessage],
  );

  // 配置游戏 Tab 国际化选项（用于 CardCommon 顶部 Segment）
  const configTabOptions = useMemo(
    () => formatOptionsMessage(tabOptions),
    [formatOptionsMessage, tabOptions],
  );

  // 下载处理
  const handleDownload = useCallback(() => {
    DBO_U.download({
      key: 'VersionAnalysisDownload',
      fileName: 'Version Analysis',
      filter: {
        ...filter,
        isConfigGame,
        channel: ['steam'],
        channel_type: 'comments',
        group_by: isConfigGame ? groupBy : undefined,
        sort_item: tableSortInfo.by,
        sort_model: tableSortInfo.order || 'desc',
      },
      auxiliaryParams: { visibleColumns, isConfigGame, groupBy: isConfigGame ? groupBy : undefined },
    });
  }, [filter, isConfigGame, visibleColumns, groupBy, tableSortInfo]);

  // CardCommon operations：设置图标
  const operations: operation[] = useMemo(
    () => [
      {
        symbol: 'setting',
        icon: 'jichushezhi',
        tip: formatMessage({ id: 'Metrics Settings' }),
        handleClick: () => {
          setStagingColumns([...visibleColumns]);
          setSettingVisible(true);
        },
      },
    ],
    [visibleColumns, formatMessage],
  );

  return (
    <>
      <CardCommon
        className="dbo-version-analysis-cardCommon"
        title={formatMessage({ id: 'Overall Performance' })}
        loadingHeight={400}
        emptyHeight={400}
        logBtnId="VersionAnalysisTable"
        operations={operations}
        alwaysShowOperations
        hasDownload
        handleDownloadOnClick={handleDownload}
        filters={isConfigGame ? configTabOptions : undefined}
        filterClassName={isConfigGame ? 'dbo-version-analysis-dune-segment' : undefined}
        filterInfo={
          isConfigGame
            ? [groupBy, (value: string) => setGroupBy(value)]
            : undefined
        }
      >
        {isConfigGame ? (
          <Tabs
            className={ClassSpecial.TAB_NONE}
            tabs={tabOptions.map(opt => ({ id: opt.value, label: opt.text }))}
            activeId={groupBy}
            destroyInactiveTabPanel={false}
          >
            {tabOptions.map((item) => (
              <TabPanel forceRender key={item.value} id={item.value}>
                <VersionTable
                  filter={filter}
                  groupBy={item.value}
                  isSelected={groupBy === item.value}
                  visibleColumns={visibleColumns}
                  versionConfig={versionConfig}
                  sortInfo={tableSortInfo}
                  onSortInfoChange={setTableSortInfo}
                />
              </TabPanel>
            ))}
          </Tabs>
        ) : (
          <VersionTable
            filter={filter}
            visibleColumns={visibleColumns}
            sortInfo={tableSortInfo}
            onSortInfoChange={setTableSortInfo}
          />
        )}
      </CardCommon>

      {/* 显示设置弹窗 */}
      <Modal
        visible={settingVisible}
        caption={formatMessage({ id: 'Metrics Settings' })}
        onClose={() => {
          setStagingColumns([...visibleColumns]);
          setSettingVisible(false);
        }}
        size="l"
      >
        <Modal.Body>
          <div className="dbo-version-display-settings__checkbox-group">
            <span className="dbo-version-display-settings__checkbox-group-title">
              {formatMessage({ id: 'Show Metrics' })}
            </span>
            <Checkbox.Group
              value={stagingColumns}
              onChange={(cols) => cols.length > 0 && setStagingColumns(cols)}
            >
              {checkboxOptions.map((opt) => (
                <Checkbox key={opt.value} name={opt.value}>
                  {opt.label}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="primary"
            onClick={() => {
              setVisibleColumns([...stagingColumns]);
              setSettingVisible(false);
            }}
          >
            {formatMessage({ id: 'Confirm' })}
          </Button>
          <Button
            type="weak"
            onClick={() => {
              setSettingVisible(false);
            }}
          >
            {formatMessage({ id: 'Cancel' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default React.memo(VersionAnalysisTable);
